/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TCreateSessionParams } from "@tivocorp/data-domain-lib";

import { DeviceModel } from "../../device";
import { SessionInteractor } from "../../interactor";
import { PlaybackContentType, SessionState } from "../enums";
import type { ISessionCreateModel } from "../interfaces";
import { StreamingSessionImpl } from "./StreamingSessionImpl";

/**
 * Streaming session implementation for NDVR content types:
 * NDVR_LIVE (recording in progress), NDVR_RECORDED (completed recording).
 */
export class NdvrStreamingSessionImpl extends StreamingSessionImpl {
  protected readonly TAG: string = "NdvrStreamingSessionImpl";

  public async createStreamingSession(
    sessionCreateModel: ISessionCreateModel
  ): Promise<void> {
    const { drmType, transportEncodingType, recordingWatchAction } =
      sessionCreateModel;

    const kernel =
      recordingWatchAction !== undefined &&
      recordingWatchAction !== null &&
      typeof recordingWatchAction === "object" &&
      "kernel" in recordingWatchAction
        ? (recordingWatchAction.kernel as Record<string, unknown> | undefined)
        : undefined;
    const kernelFields: Record<string, unknown> = kernel || {};

    const recordingProviderPartnerId =
      kernelFields.recordingProviderPartnerId as string | undefined;
    const recordingId = kernelFields.recordingId as string | undefined;
    const partnerStationId = kernelFields.partnerStationId as
      | string
      | undefined;

    if (!recordingProviderPartnerId && !recordingId && !partnerStationId) {
      this.log(
        "error",
        "createStreamingSession(): missing required fields in recordingWatchAction"
      );
      this.mError = this.getPlaybackError("MISSING_RECORDING_PARAMS");
      this.mSessionState = SessionState.ERROR;
      return;
    }

    try {
      const tsn = await DeviceModel.getTiVoSettings().getBodyId();
      const sessionParams: TCreateSessionParams = {
        deviceType:
          DeviceModel.getTiVoSettings().getDeviceType() as TCreateSessionParams["deviceType"],
        drmType: (drmType || "widevine") as TCreateSessionParams["drmType"],
        partnerId: recordingProviderPartnerId || "",
        partnerServicesCustomerId: await DeviceModel.getPartnerCustomerId(),
        partnerServicesBodyId: await DeviceModel.getCaDeviceId(),
        partnerRecordingId: recordingId || "",
        partnerStationId: partnerStationId || "",
        transportEncodingType:
          transportEncodingType as TCreateSessionParams["transportEncodingType"],
        tsn
      };

      this.log(
        "info",
        `createStreamingSession(): contentType=${sessionCreateModel.playbackContentType}`
      );

      const session = await new SessionInteractor().createSession(
        sessionParams
      );
      this.mDomainSession = session;
      this.mSession = {
        playlistUrl: NdvrStreamingSessionImpl.resolvePlaylistUrl(
          session.playlistUrl,
          sessionCreateModel.playUri
        ),
        trickModeRestrictions:
          (session.trickModeRestrictions as Record<string, unknown>) || {},
        firstKeepAliveSeconds: session.firstKeepAliveSeconds,
        keepAliveIntervalSeconds: session.keepAliveIntervalSeconds || 0,
        localityId: NdvrStreamingSessionImpl.resolveLocalityId(session)
      };
      this.mSessionState = SessionState.SUCCESS;
    } catch (error: unknown) {
      this.log("error", "createStreamingSession()", error);
      let errorMessage = "";
      if (typeof error === "object" && error !== null && "message" in error) {
        const errorObject = error as { message: unknown };
        if (typeof errorObject.message === "string") {
          errorMessage = errorObject.message;
        }
      }

      if (errorMessage === "Network unavailable") {
        this.mSessionState = SessionState.UNREACHABLE;
      } else {
        this.mError = this.getSessionError(
          error,
          PlaybackContentType.NDVR_LIVE
        );
        this.mSessionState = SessionState.ERROR;
      }
    }
  }
}
